import React from "react";
import {
  ArrowLeft,
  Bot,
  Calendar,
  Eye,
  Globe,
  Image as ImageIcon,
  LayoutDashboard,
  Loader2,
  Mail,
  MapPin,
  Phone,
  User,
  Hammer,
  Wrench,
  X,
} from "lucide-react";
import { BillingBanner } from "../../../components/billing/BillingBanner";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { ImageLightbox } from "../../../components/ui/ImageLightbox";
import { Separator } from "../../../components/ui/separator";
import { cn } from "../../../lib/utils";
import type { MainAppPageProps } from "../types";
import {
  ChatInterface,
  type ChatMessage,
} from "../../../components/chat/ChatInterface";
import { WelcomeMessageCard } from "../../../components/chat/WelcomeMessageCard";
import { LivePreviewPanel } from "../../../components/dashboard/LivePreviewPanel";
import { HomeProductShortcuts } from "../../../components/dashboard/HomeProductShortcuts";
import { DashboardWelcomeTour } from "../../../components/dashboard/DashboardWelcomeTour";
import { SiteLiveModal } from "../../../components/dashboard/SiteLiveModal";
import {
  ImageAssignmentModal,
  parsePhotoAssignmentFocus,
  type PhotoAssignmentSection,
} from "../../../components/dashboard/ImageAssignmentModal";
import { hasDashboardWelcomeTourBeenSeen } from "../../../lib/dashboardWelcomeTourStorage";
import {
  hasSiteLiveModalBeenSeen,
  markSiteLiveModalSeen,
} from "../../../lib/siteLiveModalStorage";
import {
  PROJECT_IMAGE_MAX_UPLOAD_BYTES,
  projectImageOversizeMessage,
} from "../../../lib/projectImageUploadLimits";
import type { Neighborhood } from "../../../../types/neighborhood";
import {
  EMPTY_NEIGHBORHOODS,
  useDashboardMobileTabsHeightVar,
  useNeighborhoodSelector,
} from "../../../hooks";
import { DesignPreviewCard } from "../../../components/design/DesignPreviewCard";
import { useAppToast } from "../../../components/feedback/AppToastProvider";
import type { DesignTokenOverrides } from "../../../../types/design";
import type { UserDisplay } from "../types";
import {
  postDashboardUiTurn,
  type PostDashboardUiTurnOptions,
} from "../../../lib/postDashboardUiTurn";
import { shouldRefreshLivePreview } from "../../../lib/livePreviewRefresh";

type PendingUpload = {
  suggestedTitle: string;
};

type PendingAction = {
  type: "show_neighborhood_selector";
  city?: string;
  neighborhoods?: Neighborhood[];
  mode?: "add" | "replace";
  preselected_names?: string[];
  center?: { lat: number; lng: number };
  country?: string;
} | null;

type ProjectImage = {
  id: number;
  title: string;
  category: string | null;
  description: string | null;
  thumbUrl: string;
  imageUrl: string;
  largeUrl: string;
  aboutSection?: boolean;
  aboutSectionPosition?: number | null;
  showInProjectGallery?: boolean;
  usedAsProfilePhoto?: boolean;
  usedAsBusinessLogo?: boolean;
};

const MAX_ABOUT_SECTION_PHOTOS = 6;
const MAX_SITE_GALLERY_PHOTOS = 6;

function dashboardChatErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
}

type DashboardPageProps = Pick<
  MainAppPageProps,
  | "csrfToken"
  | "googleMapsApiKey"
  | "routes"
  | "businessForm"
  | "siteUrl"
  | "contactEmail"
  | "unreadQuoteCount"
  | "initialMessage"
  | "initialPrompt"
  | "agentError"
  | "billing"
  | "designConfig"
> & {
  userDisplay?: UserDisplay | null;
  onUserDisplayChange?: (updates: Partial<UserDisplay>) => void;
  hasWebsite?: boolean;
};

const DashboardPage: React.FC<DashboardPageProps> = ({
  csrfToken,
  googleMapsApiKey,
  routes,
  businessForm: initialBusinessForm,
  siteUrl,
  contactEmail,
  initialMessage,
  initialPrompt,
  agentError,
  billing,
  designConfig: initialDesignConfig,
  userDisplay,
  onUserDisplayChange,
  hasWebsite = false,
}) => {
  const { pushToast, showError } = useAppToast();
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [input, setInput] = React.useState(initialPrompt ?? "");
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [businessForm, setBusinessForm] = React.useState(initialBusinessForm);
  const [pendingUpload, setPendingUpload] =
    React.useState<PendingUpload | null>(null);
  const [pendingAction, setPendingAction] = React.useState<PendingAction>(null);
  const [projectImages, setProjectImages] = React.useState<ProjectImage[]>([]);
  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);
  const [canAddMoreImages, setCanAddMoreImages] = React.useState(true);
  const [showPreview, setShowPreview] = React.useState(false);
  const [currentDesignConfig, setCurrentDesignConfig] =
    React.useState<DesignTokenOverrides>(initialDesignConfig ?? {});
  const [mobileTab, setMobileTab] = React.useState<
    "overview" | "chat" | "preview"
  >("overview");
  const [previewRefreshKey, setPreviewRefreshKey] = React.useState(0);
  const [viewSitePulseToken, setViewSitePulseToken] = React.useState(0);
  const [deletingImageId, setDeletingImageId] = React.useState<number | null>(
    null,
  );
  const [imageAssignmentOpen, setImageAssignmentOpen] = React.useState(false);
  const [imageAssignmentSection, setImageAssignmentSection] =
    React.useState<PhotoAssignmentSection>("hero");
  const [welcomeTourRun, setWelcomeTourRun] = React.useState(false);
  const [showSiteLiveModal, setShowSiteLiveModal] = React.useState(
    () => typeof window !== "undefined" && !hasSiteLiveModalBeenSeen(),
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const mobileTabsRef = React.useRef<HTMLDivElement>(null);
  const billingRestricted = Boolean(billing?.restricted);
  const jobsHref = routes.dashboardJobs ?? "/dashboard/jobs";
  const credentialsHref =
    routes.dashboardCredentials ?? "/dashboard/credentials";

  useDashboardMobileTabsHeightVar(mobileTabsRef);

  React.useLayoutEffect(() => {
    const region = document.querySelector<HTMLElement>(
      "[data-dashboard-mobile-scroll-region]",
    );
    if (!region) return;
    const mq = window.matchMedia("(max-width: 1023px)");
    const sync = () => {
      const shouldLock = mq.matches && (showPreview || mobileTab === "chat");
      if (shouldLock) {
        region.setAttribute("data-dashboard-scroll-lock", "true");
      } else {
        region.removeAttribute("data-dashboard-scroll-lock");
      }
    };
    sync();
    mq.addEventListener("change", sync);
    return () => {
      mq.removeEventListener("change", sync);
      region.removeAttribute("data-dashboard-scroll-lock");
    };
  }, [mobileTab, showPreview]);

  React.useEffect(() => {
    const loadImages = async () => {
      try {
        const projectRes = await fetch(routes.projectImages, {
          headers: { "X-CSRF-Token": csrfToken },
        });
        if (projectRes.ok) {
          const data = await projectRes.json();
          setProjectImages(data.project_images || []);
          setCanAddMoreImages((data.project_images?.length ?? 0) < 12);
        }
      } catch {
        // Silently fail
      }
    };
    loadImages();
  }, [routes.projectImages, csrfToken]);

  React.useEffect(() => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detectedTimezone) return;
    fetch(routes.dashboardTimezone, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ timezone: detectedTimezone }),
    }).catch(() => {});
  }, [routes.dashboardTimezone, csrfToken]);

  const hasSentInitialPrompt = React.useRef(false);
  const initialPromptRef = React.useRef(initialPrompt);
  const agentErrorRef = React.useRef(agentError);
  React.useEffect(() => {
    const prompt = initialPromptRef.current;
    if (prompt && !hasSentInitialPrompt.current && !agentErrorRef.current) {
      hasSentInitialPrompt.current = true;
      setMessages((prev) => [...prev, { role: "user", content: prompt }]);
      setIsProcessing(true);
      handleDashboardSubmit(prompt);
      setInput("");
    }
  }, []);

  React.useEffect(() => {
    if (initialPrompt && typeof window !== "undefined") {
      const isMobile = window.matchMedia("(max-width: 1023px)").matches;
      if (isMobile) setMobileTab("chat");
    }
  }, [initialPrompt]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const key = params.get("featureKey");
    const photoFocus = params.get("photoFocus");
    if (key === "about_images") {
      setImageAssignmentSection("about");
      setImageAssignmentOpen(true);
    }
    if (photoFocus) {
      setImageAssignmentSection(parsePhotoAssignmentFocus(photoFocus));
      setImageAssignmentOpen(true);
    }
  }, []);

  React.useEffect(() => {
    if (showPreview) {
      setWelcomeTourRun(false);
      return;
    }
    if (showSiteLiveModal) return;
    if (hasDashboardWelcomeTourBeenSeen()) return;
    const narrow =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1023px)").matches;
    if (narrow) setMobileTab("chat");
    setWelcomeTourRun(true);
  }, [showPreview, showSiteLiveModal]);

  const handleNeighborhoodConfirm = async (
    selected: Neighborhood[],
    mode?: "add" | "replace",
  ) => {
    const currentMode = mode || pendingAction?.mode || "replace";
    setPendingAction(null);
    setIsProcessing(true);

    try {
      const response = await fetch(`${routes.dashboard}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          neighborhood_confirmation: true,
          neighborhoods: selected,
          mode: currentMode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to confirm neighborhoods");
      }

      const data = await response.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.message },
      ]);
      if (data.current_business) {
        setBusinessForm(data.current_business);
      }

      const neighborhoodWebsiteChanged = data.website_changed === true;
      const neighborhoodUpdatedFields = data.updated_fields as
        | Record<string, unknown>
        | undefined;
      if (
        shouldRefreshLivePreview({
          showPreview,
          websiteChanged: neighborhoodWebsiteChanged,
          hasDesignChanges: false,
          updatedFields: neighborhoodUpdatedFields,
        })
      ) {
        setPreviewRefreshKey((k) => k + 1);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "error", content: dashboardChatErrorMessage(error) },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNeighborhoodSkip = async () => {
    setPendingAction(null);
    await handleNeighborhoodConfirm([]);
  };

  const hasNeighborhoodSelector =
    pendingAction?.type === "show_neighborhood_selector" &&
    (pendingAction.neighborhoods?.length ?? 0) > 0;

  const initialSelection =
    hasNeighborhoodSelector &&
    (pendingAction?.preselected_names?.length ?? 0) > 0
      ? pendingAction.preselected_names!
      : "none";

  const neighborhoodPlaceSearch = React.useMemo(
    () =>
      hasNeighborhoodSelector
        ? {
            placesUrl: routes.serviceAreaPlaces,
            csrfToken,
            googleMapsApiKey: googleMapsApiKey ?? undefined,
            center: pendingAction?.center ?? null,
            country: pendingAction?.country,
          }
        : undefined,
    [
      hasNeighborhoodSelector,
      routes.serviceAreaPlaces,
      csrfToken,
      googleMapsApiKey,
      pendingAction?.center,
      pendingAction?.country,
    ],
  );

  const neighborhoodSelector = useNeighborhoodSelector(
    hasNeighborhoodSelector ? (pendingAction!.city ?? "") : "",
    hasNeighborhoodSelector
      ? (pendingAction!.neighborhoods ?? EMPTY_NEIGHBORHOODS)
      : EMPTY_NEIGHBORHOODS,
    handleNeighborhoodConfirm,
    handleNeighborhoodSkip,
    initialSelection,
    neighborhoodPlaceSearch,
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isProcessing || !input.trim()) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsProcessing(true);
    setPendingAction(null);

    await handleDashboardSubmit(userMessage);
  };

  const stripDashboardPromptQueryParam = React.useCallback(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const hadPrompt = url.searchParams.has("prompt");
    const hadFeatureKey = url.searchParams.has("featureKey");
    if (!hadPrompt && !hadFeatureKey) return;
    url.searchParams.delete("prompt");
    url.searchParams.delete("featureKey");
    const qs = url.searchParams.toString();
    window.history.replaceState(
      null,
      "",
      `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`,
    );
  }, []);

  const stripPhotoFocusQueryParam = React.useCallback(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("photoFocus")) return;
    url.searchParams.delete("photoFocus");
    const qs = url.searchParams.toString();
    window.history.replaceState(
      null,
      "",
      `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`,
    );
  }, []);

  const handleImageAssignmentModalClose = React.useCallback(() => {
    setImageAssignmentOpen(false);
    stripPhotoFocusQueryParam();
  }, [stripPhotoFocusQueryParam]);

  const handleDashboardSubmit = async (userMessage: string) => {
    try {
      const response = await fetch(`${routes.dashboard}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ message: userMessage }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send message");
      }

      const data = await response.json();

      const designConfigSaved = data.design_config_saved !== false;
      const hasDesignUpdates =
        designConfigSaved &&
        data.design_updates &&
        Object.keys(data.design_updates).length > 0;
      const hasHeroUpdate = data.hero_image_updated === true;
      const hasHeroCopyUpdate = data.hero_copy_updated === true;
      const hasDesignChanges =
        hasDesignUpdates || hasHeroUpdate || hasHeroCopyUpdate;
      const websiteChanged = data.website_changed === true;
      const updatedFields = data.updated_fields as
        | Record<string, unknown>
        | undefined;

      setMessages((prev) =>
        data.message
          ? [
              ...prev,
              {
                role: "assistant",
                content: data.message,
                metadata: hasDesignChanges
                  ? {
                      designUpdate: {
                        design_updates: data.design_updates,
                        design_config: data.design_config,
                      },
                    }
                  : undefined,
              },
            ]
          : prev,
      );

      if (data.current_business) {
        setBusinessForm(data.current_business);
      }

      if (
        shouldRefreshLivePreview({
          showPreview,
          websiteChanged,
          hasDesignChanges,
          updatedFields,
        })
      ) {
        setPreviewRefreshKey((k) => k + 1);
      }

      if (hasDesignChanges) {
        setCurrentDesignConfig(data.design_config ?? {});
      }

      if (data.show_preview && !showPreview) {
        setShowPreview(true);
        setMobileTab("chat");
      }

      stripDashboardPromptQueryParam();

      if (data.action === "request_image_upload") {
        const params = data.action_params as
          | {
              suggested_title?: string;
            }
          | undefined;
        setPendingUpload({
          suggestedTitle: params?.suggested_title || "",
        });
        fileInputRef.current?.click();
      } else if (data.action === "open_image_assignment") {
        const params = data.action_params as { focus?: string } | undefined;
        setImageAssignmentSection(parsePhotoAssignmentFocus(params?.focus));
        setImageAssignmentOpen(true);
      } else if (
        data.action === "show_neighborhood_selector" &&
        data.action_params?.neighborhoods?.length > 0
      ) {
        setPendingAction({
          type: "show_neighborhood_selector",
          city: data.action_params.city,
          neighborhoods: data.action_params.neighborhoods,
          mode: data.action_params.mode || "replace",
          preselected_names: data.action_params.preselected_names ?? [],
          center: data.action_params.center,
          country: data.action_params.country,
        });
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "error", content: dashboardChatErrorMessage(error) },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuickAction = async (prompt: string) => {
    setMessages((prev) => [...prev, { role: "user", content: prompt }]);
    setIsProcessing(true);
    setPendingAction(null);
    await handleDashboardSubmit(prompt);
  };

  const appendUiTurnMessage = React.useCallback(
    async (options: PostDashboardUiTurnOptions) => {
      const data = await postDashboardUiTurn(
        routes.dashboardUiTurn,
        csrfToken,
        options,
      );
      const text = data?.message?.trim();
      if (!text) return;
      const role: ChatMessage["role"] =
        data?.role === "error" ? "error" : "assistant";
      setMessages((prev) => [...prev, { role, content: text }]);
    },
    [routes.dashboardUiTurn, csrfToken],
  );

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      setPendingUpload(null);
      return;
    }

    const fileArray = Array.from(files);

    const maxToUpload = Math.min(fileArray.length, 12 - projectImages.length);

    if (maxToUpload === 0) {
      await appendUiTurnMessage({
        turn: "project_images_max_reached",
        params: { context: "upload" },
      });
      setPendingUpload(null);
      event.target.value = "";
      return;
    }

    const oversized = fileArray
      .slice(0, maxToUpload)
      .find((f) => f.size > PROJECT_IMAGE_MAX_UPLOAD_BYTES);
    if (oversized) {
      pushToast({
        title: "Upload limit",
        description: projectImageOversizeMessage(oversized.name),
        tone: "error",
      });
      setPendingUpload(null);
      event.target.value = "";
      return;
    }

    setIsProcessing(true);
    setMessages((prev) => [
      ...prev,
      {
        role: "system",
        content: `Uploading ${maxToUpload} image${maxToUpload > 1 ? "s" : ""}...`,
      },
    ]);

    const uploadedImages: ProjectImage[] = [];
    let lastCanAddMore = true;
    let uploadErrorToastShown = false;

    for (let i = 0; i < maxToUpload; i++) {
      const file = fileArray[i];
      const defaultTitle = file?.name
        .replace(/\.[^/.]+$/, "")
        .replace(/[-_]/g, " ");
      const title =
        pendingUpload?.suggestedTitle ||
        defaultTitle ||
        `Project Photo ${projectImages.length + uploadedImages.length + 1}`;

      try {
        const formData = new FormData();
        formData.append("image", file as File);
        formData.append("title", title);

        const response = await fetch(routes.projectImages, {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken },
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          uploadedImages.push(data.project_image);
          lastCanAddMore = data.can_add_more;
        } else if (!uploadErrorToastShown) {
          const err =
            typeof data.error === "string" && data.error.trim()
              ? data.error
              : "Upload failed.";
          showError(err, "Upload failed");
          uploadErrorToastShown = true;
        }
      } catch {
        // Continue
      }
    }

    if (uploadedImages.length > 0) {
      setProjectImages((prev) => [...prev, ...uploadedImages]);
      setCanAddMoreImages(lastCanAddMore);
      if (showPreview) setPreviewRefreshKey((k) => k + 1);
      setMessages((prev) => prev.slice(0, -1));
      await appendUiTurnMessage({
        turn: "project_images_uploaded",
        params: {
          count: uploadedImages.length,
          can_add_more: lastCanAddMore,
        },
      });
      setImageAssignmentSection("gallery");
      setImageAssignmentOpen(true);
    } else {
      setMessages((prev) => prev.slice(0, -1));
      const uploadError = "Failed to upload images. Please try again.";
      showError(uploadError, "Upload failed");
      await appendUiTurnMessage({
        turn: "project_images_upload_failed",
        params: { error: uploadError },
        uiRole: "error",
      });
    }

    setIsProcessing(false);
    setPendingUpload(null);
    event.target.value = "";
  };

  const handleDeleteImage = async (img: ProjectImage) => {
    setDeletingImageId(img.id);
    try {
      const response = await fetch(`${routes.projectImages}/${img.id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setProjectImages((prev) => prev.filter((i) => i.id !== img.id));
        setCanAddMoreImages(true);
        setBusinessForm((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          if (next.heroProjectImageId === img.id) {
            next.heroProjectImageId = null;
            next.heroBannerUrl = null;
          }
          if (next.logoProjectImageId === img.id) {
            next.logoProjectImageId = null;
          }
          if (next.profileProjectImageId === img.id) {
            next.profileProjectImageId = null;
          }
          if (
            next.heroProjectImageId === prev.heroProjectImageId &&
            next.heroBannerUrl === prev.heroBannerUrl &&
            next.logoProjectImageId === prev.logoProjectImageId &&
            next.profileProjectImageId === prev.profileProjectImageId
          ) {
            return prev;
          }
          return next;
        });
        onUserDisplayChange?.({
          ...(businessForm?.logoProjectImageId === img.id
            ? { hasLogo: false, logoUrl: null }
            : {}),
          ...(businessForm?.profileProjectImageId === img.id
            ? { hasCustomPhoto: false, avatarUrl: null }
            : {}),
        });
        if (showPreview) setPreviewRefreshKey((k) => k + 1);
      } else {
        const deleteError =
          data.error || "Failed to remove image. Please try again.";
        await appendUiTurnMessage({
          turn: "project_image_delete_failed",
          params: { error: deleteError },
          uiRole: "error",
        });
      }
    } catch {
      await appendUiTurnMessage({
        turn: "project_image_delete_failed",
        params: { error: "Failed to remove image. Please try again." },
        uiRole: "error",
      });
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleUploadClick = async () => {
    if (!canAddMoreImages) {
      await appendUiTurnMessage({
        turn: "project_images_max_reached",
        params: { context: "picker_click" },
      });
      return;
    }
    setPendingUpload({
      suggestedTitle: "",
    });
    fileInputRef.current?.click();
  };

  const openSitePhotosModal = (sec: PhotoAssignmentSection) => {
    setImageAssignmentSection(sec);
    setImageAssignmentOpen(true);
  };

  const handleImageAssignmentSaved = async (payload: {
    projectImages: ProjectImage[];
    heroBannerUrl?: string | null;
    heroProjectImageId?: number | null;
    logoUrl?: string | null;
    logoProjectImageId?: number | null;
    avatarUrl?: string | null;
    profileProjectImageId?: number | null;
  }) => {
    setProjectImages(payload.projectImages);
    setBusinessForm((prev) =>
      prev
        ? {
            ...prev,
            heroBannerUrl: payload.heroBannerUrl ?? prev.heroBannerUrl,
            heroProjectImageId:
              payload.heroProjectImageId ?? prev.heroProjectImageId,
            logoProjectImageId:
              payload.logoProjectImageId ?? prev.logoProjectImageId,
            profileProjectImageId:
              payload.profileProjectImageId ?? prev.profileProjectImageId,
          }
        : prev,
    );
    onUserDisplayChange?.({
      avatarUrl: payload.avatarUrl ?? null,
      logoUrl: payload.logoUrl ?? null,
      hasCustomPhoto: Boolean(payload.profileProjectImageId),
      hasLogo: Boolean(payload.logoProjectImageId),
    });
    if (showPreview) setPreviewRefreshKey((k) => k + 1);
    setMobileTab("chat");
    await appendUiTurnMessage({ turn: "photo_placements_saved" });
  };

  const triggerViewSiteTabPulse = React.useCallback(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 1023px)").matches) return;
    setViewSitePulseToken((token) => token + 1);
  }, []);

  const togglePreview = () => {
    setShowPreview((prev) => {
      if (!prev) {
        setMobileTab("chat");
        triggerViewSiteTabPulse();
      } else {
        setMobileTab("overview");
      }
      return !prev;
    });
  };

  /** Welcome card "Edit Website" — open preview (or pulse if already open). */
  const openLivePreview = React.useCallback(async () => {
    const enteringPreview = !showPreview;
    if (enteringPreview) {
      setMobileTab("chat");
      try {
        const response = await fetch(routes.dashboardEnterLivePreview, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
        });
        if (response.ok) {
          const data = (await response.json()) as { message?: string };
          const assistantMessage = data.message?.trim();
          if (assistantMessage) {
            setMessages((prev) => [
              ...prev,
              { role: "assistant", content: assistantMessage },
            ]);
          }
        }
      } catch {
        // Preview still opens; agent thread may catch up on the next chat turn.
      }
    }
    setShowPreview(true);
    triggerViewSiteTabPulse();
  }, [
    showPreview,
    triggerViewSiteTabPulse,
    routes.dashboardEnterLivePreview,
    csrfToken,
  ]);

  const chatTitle = "ToolKit Assistant";
  const chatSubtitle = showPreview
    ? "Update business details or your site look — background, brand colors, fonts, and more."
    : "Ask me to update services, photos, business details, or your site look (background, colors, fonts).";

  const chatInputPrefix = (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="shrink-0 rounded-xl"
      onClick={handleUploadClick}
      disabled={!!agentError || isProcessing}
      title={
        canAddMoreImages ? "Upload project image" : "Maximum images reached"
      }
    >
      <ImageIcon className="h-5 w-5" />
    </Button>
  );

  const previewToggle = billingRestricted ? null : (
    <button
      type="button"
      onClick={togglePreview}
      className={cn(
        "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all cursor-pointer",
        showPreview
          ? "bg-premium-50 text-premium-700 border-premium-200 hover:bg-premium-100"
          : "bg-muted/60 text-muted-foreground border-border hover:bg-muted",
      )}
    >
      {showPreview ? (
        <>
          <ArrowLeft className="w-3 h-3" />
          Back
        </>
      ) : (
        <>
          <Eye className="w-3 h-3" />
          Live Preview
        </>
      )}
    </button>
  );

  const chatInterface = (
    <ChatInterface
      messages={messages}
      input={input}
      onInputChange={setInput}
      onSend={handleSubmit}
      isProcessing={isProcessing}
      agentError={agentError}
      errorTitle="AI assistant unavailable"
      placeholder="Ask the assistant anything..."
      pendingActionPlaceholder="Choose the towns and areas you serve in the checklist above, then confirm."
      header={{
        title: chatTitle,
        subtitle: chatSubtitle,
      }}
      headerExtra={previewToggle}
      containerClassName="flex flex-1 flex-col min-h-0"
      messagesClassName="flex-1 min-h-0 space-y-6 overflow-y-auto p-4 sm:p-6"
      inputPrefix={chatInputPrefix}
      welcomeCard={
        <WelcomeMessageCard
          businessForm={businessForm}
          contactEmail={contactEmail}
          onQuickAction={handleQuickAction}
          onEnterLivePreview={openLivePreview}
          isProcessing={isProcessing}
          billingRestricted={billingRestricted}
          jobsHref={jobsHref}
          credentialsHref={credentialsHref}
        />
      }
      userAvatar={userDisplay?.avatarUrl}
      userName={userDisplay?.userName}
      renderMessageSuffix={(msg) => {
        const update = msg.metadata?.designUpdate as
          | { design_updates: DesignTokenOverrides }
          | undefined;
        if (!update?.design_updates) return null;
        return (
          <div className="flex justify-start pl-11">
            <div className="w-[min(340px,100%)]">
              <DesignPreviewCard updates={update.design_updates} />
            </div>
          </div>
        );
      }}
      renderPendingAction={
        hasNeighborhoodSelector
          ? () => <div className="w-full">{neighborhoodSelector.content}</div>
          : undefined
      }
      bottomBarAction={
        hasNeighborhoodSelector ? neighborhoodSelector.bottomBar : undefined
      }
    />
  );

  const leftTabActive = showPreview
    ? mobileTab === "preview"
    : mobileTab === "overview";

  /** Narrow: fill viewport and clip outer scroll (preview any tab, or assistant). */
  const mobileGridFill = showPreview || mobileTab === "chat";
  const mobileOverviewScroll = !showPreview && mobileTab === "overview";

  return (
    <div
      data-dashboard-page-root
      className="relative flex w-full min-h-0 flex-1 flex-col lg:min-h-0"
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        multiple
        className="hidden"
      />

      {/* Mobile tab bar — fixed below header so Safari still works with scroll-region overflow lock */}
      <div
        ref={mobileTabsRef}
        className="flex shrink-0 border-b border-border bg-card shadow-sm lg:hidden z-40 max-lg:fixed max-lg:left-0 max-lg:right-0 max-lg:top-[var(--ds-app-header-height)] max-lg:w-screen"
        data-dashboard-mobile-tabs
      >
        <button
          onClick={() => setMobileTab(showPreview ? "preview" : "overview")}
          className={cn(
            "flex-1 py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors",
            leftTabActive
              ? "shadow-[inset_0_-2px_0_0_var(--color-premium-500)] text-premium-600"
              : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          {showPreview ? (
            <span
              key={`view-site-pulse-${viewSitePulseToken}`}
              data-testid="dashboard-view-site-tab-label"
              className={cn(
                "flex items-center gap-2",
                viewSitePulseToken > 0 && "ds-view-site-tab-pulse",
              )}
            >
              <Globe className="w-4 h-4" />
              View Site
            </span>
          ) : (
            <>
              <LayoutDashboard className="w-4 h-4" />
              Overview
            </>
          )}
        </button>
        <button
          onClick={() => setMobileTab("chat")}
          className={cn(
            "flex-1 py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors",
            mobileTab === "chat"
              ? "shadow-[inset_0_-2px_0_0_var(--color-premium-500)] text-premium-600"
              : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          <Bot className="w-4 h-4" />
          Assistant
        </button>
      </div>

      {/* Reserves flow space while tabs are `position: fixed` on narrow viewports */}
      <div
        aria-hidden
        className="hidden shrink-0 max-lg:block lg:hidden"
        style={{
          height: "var(--ds-dashboard-mobile-tabs-height, 3.125rem)",
        }}
      />

      {!showPreview && mobileTab === "overview" ? (
        <div className="flex shrink-0 flex-col gap-5">
          <BillingBanner billing={billing} billingRoute={routes.billing} />
        </div>
      ) : null}

      <div
        className={cn(
          "pt-4 flex min-h-0 flex-1 flex-col items-stretch lg:flex-1 lg:flex-row lg:min-h-0 lg:h-full lg:overflow-hidden sm:h-full",
          mobileGridFill &&
            "max-lg:flex-1 max-lg:min-h-0 max-lg:overflow-hidden max-lg:h-full",
          mobileOverviewScroll &&
            "max-lg:h-auto max-lg:flex-none max-lg:overflow-visible",
          showPreview && "gap-4",
          !showPreview && "gap-6",
        )}
      >
        {/* ── Left column: Overview or Live Preview ─────── */}
        <div
          className={cn(
            "flex min-h-0 flex-col overflow-hidden",
            showPreview
              ? "w-full min-h-0 flex-1 max-lg:min-h-0 lg:flex-1"
              : cn(
                  "w-full gap-6 lg:w-[380px] lg:shrink-0",
                  mobileOverviewScroll
                    ? "max-lg:flex-none"
                    : "max-lg:min-h-0 max-lg:flex-1",
                ),
            leftTabActive ? "flex" : "hidden lg:flex",
          )}
        >
          {showPreview ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              <LivePreviewPanel
                siteUrl={siteUrl}
                onBack={togglePreview}
                refreshKey={previewRefreshKey}
              />
            </div>
          ) : (
            <>
              <div className="shrink-0">
                <h1 className="ds-page-title">Home</h1>
                <HomeProductShortcuts
                  jobsHref={jobsHref}
                  credentialsHref={credentialsHref}
                  siteUrl={siteUrl}
                  billingRestricted={billingRestricted}
                  onEditSite={billingRestricted ? undefined : togglePreview}
                />
              </div>

              {/* Business Profile — only this block scrolls when the column is height-limited */}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto lg:overscroll-contain">
                  <div className="bg-card rounded-2xl border border-border shadow-sm p-5 md:p-6 space-y-5">
                    <div className="flex items-center justify-between">
                      <h2 className="font-bold text-lg text-foreground">
                        Business Profile
                      </h2>
                      <a
                        href={routes.features}
                        className="text-xs font-semibold text-premium-600 hover:text-premium-700 transition-colors"
                        data-tour="dashboard-features-link"
                      >
                        View All Features
                      </a>
                    </div>

                    <div className="space-y-4">
                      <InfoRow
                        icon={Hammer}
                        label="Business Name"
                        value={businessForm?.businessName}
                      />
                      <InfoRow
                        icon={User}
                        label="Owner"
                        value={businessForm?.ownerName}
                      />
                      <InfoRow
                        icon={Phone}
                        label="Phone"
                        value={businessForm?.phone}
                      />
                      <InfoRow
                        icon={Mail}
                        label="Email"
                        value={
                          (businessForm?.businessEmail || contactEmail) ??
                          undefined
                        }
                      />
                      <InfoRow
                        icon={MapPin}
                        label="Service Area"
                        value={businessForm?.serviceArea}
                      />
                      {businessForm?.serviceAreas && (
                        <div className="ml-7">
                          <dt className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                            Also Serving
                          </dt>
                          <dd className="text-sm font-medium text-muted-foreground">
                            {businessForm.serviceAreas
                              .split("\n")
                              .slice(0, 8)
                              .join(", ")}
                            {businessForm.serviceAreas.split("\n").length >
                              8 && (
                              <span>
                                {" "}
                                +
                                {businessForm.serviceAreas.split("\n").length -
                                  8}{" "}
                                more
                              </span>
                            )}
                          </dd>
                        </div>
                      )}

                      {businessForm?.services && (
                        <ServicesRow services={businessForm.services} />
                      )}

                      {businessForm?.aboutBlurb && (
                        <div className="ml-7">
                          <dt className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                            About
                          </dt>
                          <dd className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                            {businessForm.aboutBlurb}
                          </dd>
                        </div>
                      )}
                      {businessForm?.testimonials && (
                        <div className="ml-7">
                          <dt className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                            Testimonials
                          </dt>
                          <dd className="text-xs text-muted-foreground">
                            {
                              businessForm.testimonials
                                .split("\n")
                                .filter(Boolean).length
                            }{" "}
                            on file
                          </dd>
                        </div>
                      )}
                      {businessForm?.availabilityEnabled &&
                        businessForm?.availabilityWindows && (
                          <InfoRow
                            icon={Calendar}
                            label="Quote visit booking"
                            value={businessForm.availabilityWindows}
                          />
                        )}
                    </div>

                    <Separator className="bg-border" />

                    <div className="space-y-4">
                      <div>
                        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Photos
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          All images upload here (5&nbsp;MB max each). Use
                          Manage placements to configure all website photos.
                        </p>
                      </div>

                      <div>
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={handleUploadClick}
                              disabled={!!agentError || isProcessing}
                            >
                              Add photos
                            </Button>
                            {projectImages.length > 0 && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => openSitePhotosModal("hero")}
                                disabled={!!agentError || isProcessing}
                              >
                                Manage placements
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {projectImages.map((img, index) => (
                            <div
                              key={img.id}
                              className="relative w-16 h-16 overflow-hidden rounded-lg border border-border bg-muted"
                            >
                              <button
                                type="button"
                                className="w-full h-full focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 rounded-lg"
                                onClick={() => setLightboxIndex(index)}
                                aria-label={`Preview ${img.title}`}
                              >
                                <img
                                  src={img.thumbUrl}
                                  alt={img.title}
                                  className="h-full w-full object-cover cursor-pointer"
                                />
                              </button>
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute right-0.5 top-0.5 h-5 w-5 rounded-full"
                                onClick={() => handleDeleteImage(img)}
                                disabled={deletingImageId === img.id}
                                aria-label={`Remove ${img.title}`}
                                title="Remove image"
                              >
                                {deletingImageId === img.id ? (
                                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                ) : (
                                  <X className="h-2.5 w-2.5" />
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Right column: Chat (always stays on the right) */}
        <div
          className={cn(
            "flex flex-col min-w-0 min-h-0 overflow-hidden rounded-2xl border border-border bg-card shadow-card",
            showPreview
              ? "w-full max-lg:min-h-0 max-lg:flex-1 lg:w-[420px] lg:shrink-0"
              : "flex-1",
            mobileTab === "chat" ? "flex" : "hidden lg:flex",
          )}
          data-tour="dashboard-chat"
        >
          {chatInterface}
        </div>
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={projectImages.map((img) => ({
            src: img.largeUrl || img.imageUrl,
            alt: img.title,
          }))}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}

      <ImageAssignmentModal
        open={imageAssignmentOpen}
        onClose={handleImageAssignmentModalClose}
        initialSection={imageAssignmentSection}
        projectImages={projectImages}
        heroProjectImageId={businessForm?.heroProjectImageId}
        profileProjectImageId={businessForm?.profileProjectImageId}
        logoProjectImageId={businessForm?.logoProjectImageId}
        maxAboutPhotos={MAX_ABOUT_SECTION_PHOTOS}
        maxGalleryPhotos={MAX_SITE_GALLERY_PHOTOS}
        csrfToken={csrfToken}
        assignUrl={routes.projectImagesAssign}
        uploadUrl={routes.projectImages}
        canAddMoreImages={canAddMoreImages}
        onImagesUploaded={(images) => {
          setProjectImages((prev) => {
            const next = [...prev, ...(images as ProjectImage[])];
            setCanAddMoreImages(next.length < 12);
            return next;
          });
        }}
        onSaved={(payload) =>
          handleImageAssignmentSaved({
            ...payload,
            projectImages: payload.projectImages as ProjectImage[],
          })
        }
      />

      <DashboardWelcomeTour
        run={welcomeTourRun}
        onClose={() => setWelcomeTourRun(false)}
        setMobileTab={setMobileTab}
      />

      {siteUrl && (
        <SiteLiveModal
          open={showSiteLiveModal}
          siteUrl={siteUrl}
          onClose={() => {
            markSiteLiveModalSeen();
            setShowSiteLiveModal(false);
          }}
        />
      )}
    </div>
  );
};

const InfoRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value?: string;
}) => (
  <div className="flex items-start gap-3">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <div className="flex-1 overflow-hidden">
      <dt className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
        {label}
      </dt>
      <dd className="text-sm font-medium text-foreground truncate">
        {value || "—"}
      </dd>
    </div>
  </div>
);

const ServicesRow = ({ services }: { services: string }) => {
  const list = services
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  if (list.length === 0) return null;
  const display = list.slice(0, 6);
  const remainder = list.length - 6;
  return (
    <div className="flex items-start gap-3">
      <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 overflow-hidden">
        <dt className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
          Services
        </dt>
        <dd className="flex flex-wrap gap-1.5">
          {display.map((service) => (
            <Badge
              key={service}
              variant="secondary"
              className="text-[11px] font-medium"
            >
              {service}
            </Badge>
          ))}
          {remainder > 0 && (
            <Badge variant="outline" className="text-[11px]">
              +{remainder} more
            </Badge>
          )}
        </dd>
      </div>
    </div>
  );
};

export default DashboardPage;
